import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import i18n from 'i18next';
import { useCookies } from 'react-cookie';
import { createPortal } from 'react-dom';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import {
    Outlet,
    useLoaderData,
    useLocation,
} from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import { JourneyVariant } from '@r6digital/rapidstor-types/react/entities/Journey';
import { en } from '@/core/translations/en';
import {
    CorporationCode,
    getCorpTranslations,
    parseSnippetTranslations,
    unflattenTranslations,
} from '@/core/translations/helpers';
import { fetchTranslationChain } from '@/core/utils/application';
import { pt_pt } from '@/core/translations/pt-pt';
import { vi_vn } from '@/core/translations/vi-vn';
import { zh_hk } from '@/core/translations/zh-hk';
import Logger from '@/core/utils/logging';

import DevConfigModal from '@/components/dev-config-modal/DevConfigModal';
import ContactCard from '@/core/components/elements/contact-card';
import Navbar from '@/core/components/elements/navbar';
import ReservationRestore from '@/core/components/ReservationRestore';
import DetailsModal from '@/standard-unit-listing/components/details-modal';

import { dispatchEvent } from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContextProvider } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContextProvider } from '@/features/rapidstor/providers/JourneyProvider';

import { injectCustomCss, injectSiteTheme } from '../utils/theme';

export default function Root() {
    const [refreshTick, setRefreshTick] = useState(0); // eslint-disable-line @typescript-eslint/no-unused-vars
    const location = useLoaderData() as Location;
    const [agentDetailsCookie] = useCookies(['agentDetails']);
    const routerLocation = useLocation();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const deepMerge = useCallback((target: any, source: any) => {
        for (const key in source) {
            if (source[key] instanceof Object) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                deepMerge(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
        return target;
    }, []);

    useEffect(() => {
        Logger.info(`navigator.language: ${navigator.language}`);
    }, []);

    // Resolve the active language once so the snippet merge, i18n init, and the
    // DB chain all target the same language. rapidstor_options.language is the
    // single source of truth (it defaults to 'en' during bootstrap);
    // navigator.language is only a fallback if it is ever unset.
    const lang = window.rapidstor_settings?.rapidstor_options?.language
        || navigator.language
        || 'en';

    // Snippet-level overrides, decoded once from the JSON string on the config.
    // These are the ultimate override and are applied at startup; the DB chain is
    // fetched and merged beneath them after the app starts (see effect below).
    const snippetModel = useMemo(
        () => parseSnippetTranslations(
            window.rapidstor_settings?.app_config?.customTranslations,
        ),
        [],
    );

    const resources = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const built: Record<string, any> = {
            en: deepMerge(
                en,
                getCorpTranslations(location.corpCode as CorporationCode),
            ),
            'zh-HK': deepMerge(
                zh_hk,
                getCorpTranslations(location.corpCode as CorporationCode),
            ),
            'pt-PT': deepMerge(
                pt_pt,
                getCorpTranslations(location.corpCode as CorporationCode),
            ),
            'vi-VN': deepMerge(
                vi_vn,
                getCorpTranslations(location.corpCode as CorporationCode),
            ),
        };

        // Apply the snippet overrides for the resolved language on top of base +
        // bundled-corp. Keys absent here fall through to the bundled defaults.
        const snippet = snippetModel[lang] ?? {};
        if (Object.keys(snippet).length) {
            built[lang] = deepMerge(
                built[lang] ?? {},
                unflattenTranslations(snippet),
            );
        }

        return built;
    }, [location.corpCode, deepMerge, snippetModel, lang]);

    useMemo(() => {
        i18n.use(initReactI18next).init({
            lng: lang,
            resources,
            fallbackLng: 'en',
            interpolation: {
                escapeValue: false,
            },
            // Re-render translated components when resources are added at runtime
            // (the DB chain merged in by the effect below).
            react: {
                bindI18nStore: 'added',
            },
        });
    }, [resources, lang]);

    /**
     * After the app has started, fetch the DB translation chain
     * (language < corp < location) and merge it into i18next beneath the snippet
     * overrides (snippet stays on top). Non-blocking — the app already rendered
     * with the bundled defaults + snippet.
     */
    useEffect(() => {
        let cancelled = false;

        fetchTranslationChain(lang).then((chain) => {
            if (cancelled || !chain || !Object.keys(chain).length) return;

            // Re-apply the snippet on top of the chain so it keeps winning.
            const snippet = snippetModel[lang] ?? {};
            const merged = unflattenTranslations({ ...chain, ...snippet });

            Object.entries(merged).forEach(([namespace, bundle]) => {
                i18n.addResourceBundle(
                    lang,
                    namespace,
                    bundle as Record<string, unknown>,
                    true,
                    true,
                );
            });

            // `bindI18nStore: 'added'` only re-renders components that consume
            // useTranslation. The settings-text helpers in core/utils/rapidstor
            // read i18next directly, so their call sites can be plain components
            // with no i18n binding — nudge the tree so they pick the chain up.
            setRefreshTick((t: number) => t + 1);
        });

        return () => {
            cancelled = true;
        };
    }, [snippetModel, lang]);

    /**
   * Injects the site theme based on the location.
   */
    useEffect(() => {
        if (location) {
            const rapidstorMainColor = window.rapidstor_settings?.app_config?.journey?.themeColor ?? location.skin.mainColour ?? null;
            injectSiteTheme(location, rapidstorMainColor);
            injectCustomCss(window.rapidstor_settings?.app_config?.customCss);
        }
    }, [location]);

    /**
   * Dispatches the location changed event to the parent window
   */
    useEffect(() => {
        dispatchEvent({
            type: 'location-changed',
            context: {
                pathname: routerLocation.pathname,
                hash: routerLocation.hash,
            },
        });
    }, [routerLocation]);

    /**
   * Handles the agent details cookies for the UKStorage Callcenter Dashboard.
   * Pushes data through into the externalData object in the reservation payloads.
   */
    useEffect(() => {
        if (agentDetailsCookie && agentDetailsCookie.agentDetails) {
            window.rapidstor_settings.app_config.journey.agentDetails = {
                agentName: agentDetailsCookie.agentDetails.agentName,
                agentComment: agentDetailsCookie.agentDetails.agentComments,
            };
        }
    }, [agentDetailsCookie]);

    return (
        <>
            <AnalyticsContextProvider location={location}>
                <I18nextProvider i18n={i18n}>
                    <JourneyContextProvider location={location}>
                        <>
                            <ReservationRestore location={location} />
                            {window.rapidstor_settings.app_config.journey.variant
                            === JourneyVariant.JBL ? (
                                <Navbar location={location} />
                                ) : null}

                            <Outlet />

                            {createPortal(<ContactCard location={location} />, document.body)}
                            {createPortal(<DetailsModal />, document.body)}
                        </>
                    </JourneyContextProvider>
                </I18nextProvider>
            </AnalyticsContextProvider>
            {/* Dev config modal + force re-render mechanism */}
            {window.rapidstor_settings.rapidstor_options.dev && (
                <DevConfigModal
                    onForceRerender={() => {
                        setRefreshTick((t: number) => t + 1);
                    }}
                />
            )}
        </>
    );
}
